/**
 * Statistical Correlation Engine
 * Provides real statistical analysis with confidence scoring
 */

export interface CorrelationResult {
  coefficient: number; // Pearson correlation coefficient (-1 to 1)
  pValue: number; // Statistical significance
  isSignificant: boolean; // p < 0.05
  sampleSize: number;
  confidence: 'low' | 'medium' | 'high';
  quality: 'insufficient' | 'limited' | 'moderate' | 'good' | 'excellent';
}

export interface DataQuality {
  totalDays: number;
  completeDays: number;
  completenessScore: number; // 0-100
  hasMinimumData: boolean; // true if >= 30 days
  quality: 'insufficient' | 'limited' | 'moderate' | 'good' | 'excellent';
  recommendation: string;
}

/**
 * Calculate Pearson correlation coefficient
 * Measures linear relationship between two variables (-1 to 1)
 */
export function calculatePearsonCorrelation(
  xValues: number[],
  yValues: number[]
): CorrelationResult {
  const n = Math.min(xValues.length, yValues.length);
  
  if (n < 3) {
    return {
      coefficient: 0,
      pValue: 1,
      isSignificant: false,
      sampleSize: n,
      confidence: 'low',
      quality: 'insufficient'
    };
  }

  // Calculate means
  const xMean = xValues.reduce((sum, x) => sum + x, 0) / n;
  const yMean = yValues.reduce((sum, y) => sum + y, 0) / n;

  // Calculate correlation coefficient
  let numerator = 0;
  let xDenominator = 0;
  let yDenominator = 0;

  for (let i = 0; i < n; i++) {
    const xDiff = xValues[i] - xMean;
    const yDiff = yValues[i] - yMean;
    numerator += xDiff * yDiff;
    xDenominator += xDiff * xDiff;
    yDenominator += yDiff * yDiff;
  }

  const denominator = Math.sqrt(xDenominator * yDenominator);
  const coefficient = denominator === 0 ? 0 : numerator / denominator;

  // Calculate t-statistic for p-value
  const tStat = coefficient * Math.sqrt((n - 2) / (1 - coefficient * coefficient));
  const pValue = calculateTTestPValue(tStat, n - 2);

  // Determine confidence based on sample size and significance
  const confidence = getConfidenceLevel(n, pValue, Math.abs(coefficient));
  const quality = getDataQuality(n);

  return {
    coefficient,
    pValue,
    isSignificant: pValue < 0.05,
    sampleSize: n,
    confidence,
    quality
  };
}

/**
 * Calculate approximate p-value for t-test
 */
function calculateTTestPValue(t: number, df: number): number {
  // Simplified p-value approximation
  // For production, would use proper statistical libraries
  const x = df / (df + t * t);
  const p = 0.5 * incompleteBeta(df / 2, 0.5, x);
  return 2 * Math.min(p, 1 - p);
}

/**
 * Incomplete beta function (simplified approximation)
 */
function incompleteBeta(a: number, b: number, x: number): number {
  if (x === 0) return 0;
  if (x === 1) return 1;
  
  // Simplified approximation
  return Math.pow(x, a) * Math.pow(1 - x, b) / (a * beta(a, b));
}

/**
 * Beta function
 */
function beta(a: number, b: number): number {
  return (gamma(a) * gamma(b)) / gamma(a + b);
}

/**
 * Gamma function (Stirling's approximation)
 */
function gamma(z: number): number {
  return Math.sqrt(2 * Math.PI / z) * Math.pow((z / Math.E), z);
}

/**
 * Determine confidence level based on sample size and significance
 */
function getConfidenceLevel(
  sampleSize: number,
  pValue: number,
  absCoefficient: number
): 'low' | 'medium' | 'high' {
  // High confidence: large sample + significant + strong correlation
  if (sampleSize >= 60 && pValue < 0.01 && absCoefficient > 0.5) {
    return 'high';
  }
  
  // Medium confidence: adequate sample + significant
  if (sampleSize >= 30 && pValue < 0.05 && absCoefficient > 0.3) {
    return 'medium';
  }
  
  // Low confidence: everything else
  return 'low';
}

/**
 * Assess overall data quality
 */
function getDataQuality(sampleSize: number): 'insufficient' | 'limited' | 'moderate' | 'good' | 'excellent' {
  if (sampleSize < 14) return 'insufficient'; // Less than 2 weeks
  if (sampleSize < 30) return 'limited';      // Less than 1 month
  if (sampleSize < 60) return 'moderate';     // 1-2 months
  if (sampleSize < 90) return 'good';         // 2-3 months
  return 'excellent';                         // 3+ months
}

/**
 * Calculate data quality metrics
 */
export function calculateDataQuality(data: any[]): DataQuality {
  const totalDays = data.length;
  
  // Count days with complete data (has at least mood, energy, and one other metric)
  const completeDays = data.filter(day => {
    const hasBasicMetrics = day.moodScore && day.energyScore;
    const hasAdditionalData = day.sleepQuality || day.nutritionScore || day.activityCompleted;
    return hasBasicMetrics && hasAdditionalData;
  }).length;

  const completenessScore = totalDays > 0 
    ? Math.round((completeDays / totalDays) * 100) 
    : 0;

  const hasMinimumData = totalDays >= 30 && completenessScore >= 60;

  let quality: DataQuality['quality'];
  let recommendation: string;

  if (totalDays < 14) {
    quality = 'insufficient';
    recommendation = 'Keep tracking for at least 2 weeks to begin seeing patterns.';
  } else if (totalDays < 30) {
    quality = 'limited';
    recommendation = `${30 - totalDays} more days of tracking will unlock reliable pattern analysis.`;
  } else if (completenessScore < 60) {
    quality = 'moderate';
    recommendation = 'Try to complete more data fields each day for better insights.';
  } else if (totalDays < 60) {
    quality = 'good';
    recommendation = 'Great data! More tracking time will increase prediction accuracy.';
  } else {
    quality = 'excellent';
    recommendation = 'Excellent data quality! Your insights are highly reliable.';
  }

  return {
    totalDays,
    completeDays,
    completenessScore,
    hasMinimumData,
    quality,
    recommendation
  };
}

/**
 * Extract numeric values from comprehensive data for correlation analysis
 */
export function extractMetrics(data: any[], metricKey: string): number[] {
  return data
    .map(day => {
      // Handle nested properties
      const value = getNestedProperty(day, metricKey);
      return typeof value === 'number' ? value : null;
    })
    .filter((v): v is number => v !== null);
}

/**
 * Get nested property from object using dot notation
 */
function getNestedProperty(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Calculate confidence score for predictions based on multiple factors
 */
export function calculatePredictionConfidence(
  correlation: CorrelationResult,
  dataQuality: DataQuality,
  patternOccurrences: number
): number {
  // Base confidence from correlation strength
  const correlationScore = Math.abs(correlation.coefficient) * 0.4;
  
  // Data quality contribution
  const qualityScore = (dataQuality.completenessScore / 100) * 0.3;
  
  // Sample size contribution
  const sampleScore = Math.min(dataQuality.totalDays / 90, 1) * 0.2;
  
  // Pattern frequency contribution
  const frequencyScore = Math.min(patternOccurrences / 10, 1) * 0.1;

  const totalConfidence = correlationScore + qualityScore + sampleScore + frequencyScore;

  // Apply penalty if not statistically significant
  return correlation.isSignificant ? totalConfidence : totalConfidence * 0.7;
}

/**
 * Format correlation strength for display
 */
export function formatCorrelationStrength(coefficient: number): string {
  const abs = Math.abs(coefficient);
  if (abs < 0.2) return 'Very weak';
  if (abs < 0.4) return 'Weak';
  if (abs < 0.6) return 'Moderate';
  if (abs < 0.8) return 'Strong';
  return 'Very strong';
}

/**
 * Generate user-friendly explanation of correlation
 */
export function explainCorrelation(
  xLabel: string,
  yLabel: string,
  coefficient: number,
  isSignificant: boolean
): string {
  const direction = coefficient > 0 ? 'increase' : 'decrease';
  const strength = formatCorrelationStrength(coefficient).toLowerCase();
  const reliability = isSignificant ? 'This pattern is statistically significant.' : 'This pattern needs more data to confirm.';

  return `There's a ${strength} correlation: when ${xLabel} goes up, ${yLabel} tends to ${direction}. ${reliability}`;
}
